import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  editLoanPendingInstallment,
  fetchCompanies,
  fetchLoanAmortization,
  importLoanAmortizationPending,
  refreshLoanFull,
  type AmortizationInstallment,
  type AmortizationResponse,
  type Company,
  type ImportBankScheduleRow,
  type LoanRefreshFullFailure,
} from "../api";
import { formatMoney } from "../lib/grouping";
import { parseBankSchedule } from "../lib/parseBankSchedule";

const OPERATION_LABEL: Record<string, string> = {
  receipt_paid: "Recibo pagado",
  receipt_pending: "Recibo pendiente",
  receipt_partially_paid: "Pago parcial",
  receipt_scheduled: "Recibo programado",
  receipt_scheduled_estimated: "Programado (estimado)",
  receipt_scheduled_bank: "Programado (banco)",
  partial_amortization: "Amortización parcial",
  total_amortization: "Amortización total",
};

function isEstimated(op: string): boolean {
  return op === "receipt_scheduled_estimated";
}

function opLabel(op: string): string {
  return OPERATION_LABEL[op] ?? op;
}

export function LoanAmortization() {
  const { bankCode = "", contractNumber = "" } = useParams<{
    bankCode: string;
    contractNumber: string;
  }>();

  const [data, setData] = useState<AmortizationResponse | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshingFull, setRefreshingFull] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null);
  const [refreshFailure, setRefreshFailure] = useState<LoanRefreshFullFailure | null>(null);

  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState("");
  const [importRows, setImportRows] = useState<ImportBankScheduleRow[]>([]);
  const [importSkipped, setImportSkipped] = useState(0);
  const [importRateOverride, setImportRateOverride] = useState("");
  const [importSubmitting, setImportSubmitting] = useState(false);
  const [importMsg, setImportMsg] = useState<string | null>(null);

  const load = async () => {
    if (!bankCode || !contractNumber) return;
    setError(null);
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        fetchLoanAmortization(bankCode, contractNumber),
        fetchCompanies(),
      ]);
      setData(a);
      setCompanies(c.items);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankCode, contractNumber]);

  const doRefreshFull = async () => {
    if (refreshingFull) return;
    setRefreshingFull(true);
    setRefreshMsg(null);
    setRefreshFailure(null);
    try {
      const r = await refreshLoanFull(bankCode, contractNumber);
      let msg =
        `✅ Cuadro actualizado. ${r.pendingCount} pendiente${r.pendingCount === 1 ? "" : "s"} · ` +
        `${r.madeCount} realizada${r.madeCount === 1 ? "" : "s"}.`;
      if (r.estimatedCount > 0) {
        msg +=
          ` ℹ️ El banco solo devolvió ${r.rawPendingCount} cuota${r.rawPendingCount === 1 ? "" : "s"} pendiente${r.rawPendingCount === 1 ? "" : "s"}; ` +
          `las ${r.estimatedCount} restantes son estimadas por amortización francesa (fila con fondo distinto y tipo "Programado (estimado)").`;
      }
      setRefreshMsg(msg);
      await load();
    } catch (e: unknown) {
      const failure = (e as Error & { failure?: LoanRefreshFullFailure }).failure;
      if (failure) {
        setRefreshMsg(`❌ ${failure.message}`);
        setRefreshFailure(failure);
      } else {
        setRefreshMsg(`❌ ${e instanceof Error ? e.message : String(e)}`);
      }
    } finally {
      setRefreshingFull(false);
    }
  };

  const doParseImport = (text: string) => {
    setImportText(text);
    const { rows, skipped } = parseBankSchedule(text);
    setImportRows(rows);
    setImportSkipped(skipped);
  };

  const doSubmitImport = async () => {
    if (importRows.length === 0 || importSubmitting) return;
    setImportSubmitting(true);
    setImportMsg(null);
    try {
      const rateOverride = importRateOverride.trim()
        ? Number(importRateOverride.replace(",", "."))
        : undefined;
      const r = await importLoanAmortizationPending(
        bankCode,
        contractNumber,
        importRows,
        Number.isFinite(rateOverride) ? rateOverride : undefined,
      );
      setImportMsg(
        `✅ Cuadro corregido: ${r.importedCount} cuota${r.importedCount === 1 ? "" : "s"} del banco` +
          (r.projectedCount > 0
            ? ` + ${r.projectedCount} proyectada${r.projectedCount === 1 ? "" : "s"} a continuación`
            : "") +
          ` (${r.pendingCount} pendientes en total).`,
      );
      setShowImport(false);
      setImportText("");
      setImportRows([]);
      setImportSkipped(0);
      await load();
    } catch (e: unknown) {
      setImportMsg(`❌ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setImportSubmitting(false);
    }
  };

  const companyLabel = (id: string) => {
    const c = companies.find((x) => x.id === id);
    return c ? (c.alias || c.name) : id || "—";
  };

  const currency = data?.loan.currency || "EUR";

  const summary = useMemo(() => {
    if (!data) return null;
    const totalMade = data.made.reduce(
      (acc, r) => ({
        amortized: acc.amortized + r.amortized,
        interest: acc.interest + r.interest,
        total: acc.total + r.total,
      }),
      { amortized: 0, interest: 0, total: 0 },
    );
    const totalPending = data.pending.reduce(
      (acc, r) => ({
        amortized: acc.amortized + r.amortized,
        interest: acc.interest + r.interest,
        total: acc.total + r.total,
      }),
      { amortized: 0, interest: 0, total: 0 },
    );
    return { totalMade, totalPending };
  }, [data]);

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Cuadro de amortización · {data?.loan.alias || contractNumber}</h2>
          <p className="meta">
            <Link to="/prestamos">← Volver a préstamos</Link>
            {data?.loan && (
              <>
                {"  ·  "}
                {data.loan.bankName || data.loan.bankCode} · <code>{data.loan.contractNumber}</code>
                {"  ·  "}
                {companyLabel(data.loan.companyId)}
                {"  ·  "}
                Último sync: {data.loan.lastSyncedAt
                  ? new Date(data.loan.lastSyncedAt).toLocaleString()
                  : "nunca"}
              </>
            )}
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            type="button"
            onClick={doRefreshFull}
            disabled={refreshingFull || loading}
            title="Pide a Wealth Reader el cuadro completo (con todas las cuotas pendientes) llamando con only_balances=false"
          >
            {refreshingFull ? "Actualizando…" : "↻ Actualizar cuadro completo"}
          </button>
          <button type="button" onClick={load} disabled={loading}>
            {loading ? "Cargando…" : "Recargar"}
          </button>
          <button
            type="button"
            onClick={() => setShowImport((v) => !v)}
            title="Pega la tabla real que da el banco para corregir las cuotas programadas cuando la proyección automática no coincide"
          >
            {showImport ? "Cancelar importación" : "Importar cuadro del banco"}
          </button>
        </div>
      </div>
      {refreshMsg && (
        <p className={refreshMsg.startsWith("❌") ? "error" : "meta"}>{refreshMsg}</p>
      )}
      {importMsg && (
        <p className={importMsg.startsWith("❌") ? "error" : "meta"}>{importMsg}</p>
      )}

      {showImport && (
        <div className="card" style={{ margin: "1rem 0", background: "rgba(255,255,255,0.03)" }}>
          <p className="meta">
            Pega aquí la tabla que da el banco (columnas: Fecha cuota, Importe
            cuota, Amortización, Intereses, Importe pendiente de
            amortización). Sustituye las cuotas "Programadas" desde la
            primera fecha pegada en adelante; lo que quede después de la
            última fila se sigue proyectando por amortización francesa. Las
            cuotas ya contabilizadas se reconocen por fecha aproximada (±15
            días) y no pierden su estado de pago.
          </p>
          <textarea
            rows={8}
            style={{ width: "100%", fontFamily: "monospace", fontSize: "0.85em" }}
            placeholder={"12.10.2026\t7.215,34 EUR\t6.792,69 EUR\t422,65 EUR\t196.079,45 EUR"}
            value={importText}
            onChange={(e) => doParseImport(e.target.value)}
          />
          <div className="form-row" style={{ alignItems: "center", gap: "1rem", marginTop: "0.5rem" }}>
            <label>
              Tipo de interés anual % para la cola proyectada tras la última
              fila (opcional, por defecto usa el del préstamo):{" "}
              <input
                type="text"
                style={{ width: "5em" }}
                placeholder={data?.loan.lastInterestRate != null ? String(data.loan.lastInterestRate) : "—"}
                value={importRateOverride}
                onChange={(e) => setImportRateOverride(e.target.value)}
              />
            </label>
          </div>
          <p className="meta" style={{ marginTop: "0.5rem" }}>
            {importRows.length > 0
              ? `${importRows.length} fila${importRows.length === 1 ? "" : "s"} reconocida${importRows.length === 1 ? "" : "s"}` +
                (importSkipped > 0 ? ` · ${importSkipped} línea(s) ignorada(s)` : "")
              : importText.trim()
                ? "No se ha reconocido ninguna fila válida."
                : "Sin datos pegados todavía."}
          </p>
          {importRows.length > 0 && (
            <>
              <table className="data" style={{ marginTop: "0.5rem" }}>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th className="num">Cuota</th>
                    <th className="num">Amortización</th>
                    <th className="num">Intereses</th>
                    <th className="num">Capital pendiente</th>
                  </tr>
                </thead>
                <tbody>
                  {importRows.map((r) => (
                    <tr key={r.date}>
                      <td>{r.date}</td>
                      <td className="num">{formatMoney(r.total, currency)}</td>
                      <td className="num">{formatMoney(r.amortized, currency)}</td>
                      <td className="num">{formatMoney(r.interest, currency)}</td>
                      <td className="num">{formatMoney(r.pendingCapital, currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button
                type="button"
                onClick={doSubmitImport}
                disabled={importSubmitting}
                style={{ marginTop: "0.5rem" }}
              >
                {importSubmitting ? "Aplicando…" : `Aplicar ${importRows.length} fila(s) al cuadro`}
              </button>
            </>
          )}
        </div>
      )}
      {refreshFailure && refreshFailure.attempts.length > 0 && (
        <div style={{ marginBottom: "1rem", fontSize: "0.9em" }}>
          <p className="meta" style={{ margin: "0 0 0.3rem 0" }}>
            Detalle de los {refreshFailure.triedCount} intento(s):
          </p>
          <table className="data">
            <thead>
              <tr>
                <th>Credencial</th>
                <th>contract_name</th>
                <th>Usuario</th>
                <th>Contratos que devolvió</th>
                <th>Resultado</th>
              </tr>
            </thead>
            <tbody>
              {refreshFailure.attempts.map((a) => (
                <tr key={a.credentialId}>
                  <td>
                    <code>{a.credentialId}</code>
                    {a.label && (
                      <div className="meta" style={{ fontSize: "0.85em" }}>
                        {a.label}
                      </div>
                    )}
                  </td>
                  <td>
                    {a.contractName ? (
                      <code>{a.contractName}</code>
                    ) : (
                      <span className="meta">—</span>
                    )}
                  </td>
                  <td>{a.userName || <span className="meta">—</span>}</td>
                  <td>
                    {a.loansSeen && a.loansSeen.length > 0 ? (
                      a.loansSeen.map((c) => (
                        <div key={c}>
                          <code>{c}</code>
                        </div>
                      ))
                    ) : (
                      <span className="meta">— (ningún préstamo)</span>
                    )}
                  </td>
                  <td>
                    <span className="error">{a.error ?? "(sin detalle)"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="meta" style={{ marginTop: "0.5rem" }}>
            Si el contrato <code>{refreshFailure.contractNumber}</code> no
            aparece en ninguna credencial, conecta el banco{" "}
            <code>{refreshFailure.bankCode}</code> desde{" "}
            <Link to="/conectar">Conectar banco</Link> eligiendo el{" "}
            <code>contract_name</code> correcto en el widget de Wealth Reader.
          </p>
        </div>
      )}

      {data?.loan && (
        <div className="form-row" style={{ flexWrap: "wrap", gap: "1.5rem" }}>
          <div>
            <div className="meta">Capital concedido</div>
            <strong>
              {data.loan.lastGrantedCapital !== null
                ? formatMoney(data.loan.lastGrantedCapital, currency)
                : "—"}
            </strong>
          </div>
          <div>
            <div className="meta">Pendiente</div>
            <strong>
              {data.loan.lastPendingAmount !== null
                ? formatMoney(data.loan.lastPendingAmount, currency)
                : "—"}
            </strong>
          </div>
          <div>
            <div className="meta">Interés</div>
            <strong>
              {data.loan.lastInterestRate !== null ? `${data.loan.lastInterestRate} %` : "—"}
            </strong>
          </div>
        </div>
      )}

      {error && <p className="error">{error}</p>}
      {!error && data === null && !loading && <p>Cargando…</p>}

      {data && data.pending.length === 0 && data.made.length === 0 && (
        <p className="meta">
          Aún no hay cuadro persistido para este préstamo. Se rellenará en el
          próximo refresco del banco. Si quieres repoblar desde payloads
          previos, ve a Sin vincular → Reprocesar payloads.
        </p>
      )}

      {data && data.pending.length > 0 && (
        <>
          <h3 style={{ marginTop: "1.5rem" }}>Programadas ({data.pending.length})</h3>
          <AmortizationTable
            rows={data.pending}
            currency={currency}
            editable
            bankCode={bankCode}
            contractNumber={contractNumber}
            onEdited={load}
          />
          {summary && (
            <p className="meta">
              Total pendiente: capital {formatMoney(summary.totalPending.amortized, currency)} ·
              intereses {formatMoney(summary.totalPending.interest, currency)} ·
              total {formatMoney(summary.totalPending.total, currency)}
            </p>
          )}
        </>
      )}

      {data && data.made.length > 0 && (
        <>
          <h3 style={{ marginTop: "1.5rem" }}>Histórico ({data.made.length})</h3>
          <AmortizationTable rows={data.made} currency={currency} />
          {summary && (
            <p className="meta">
              Total pagado: capital {formatMoney(summary.totalMade.amortized, currency)} ·
              intereses {formatMoney(summary.totalMade.interest, currency)} ·
              total {formatMoney(summary.totalMade.total, currency)}
            </p>
          )}
        </>
      )}
    </section>
  );
}

function AmortizationTable({
  rows,
  currency,
  editable = false,
  bankCode,
  contractNumber,
  onEdited,
}: {
  rows: AmortizationInstallment[];
  currency: string;
  editable?: boolean;
  bankCode?: string;
  contractNumber?: string;
  onEdited?: () => void | Promise<void>;
}) {
  const [editingDate, setEditingDate] = useState<string | null>(null);
  const [editDate, setEditDate] = useState("");
  const [editAmortized, setEditAmortized] = useState("");
  const [editInterest, setEditInterest] = useState("");
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const startEdit = (r: AmortizationInstallment) => {
    setEditingDate(r.date);
    setEditDate(r.date);
    setEditAmortized(String(r.amortized).replace(".", ","));
    setEditInterest(String(r.interest).replace(".", ","));
    setEditError(null);
  };

  const cancelEdit = () => {
    setEditingDate(null);
    setEditError(null);
  };

  const saveEdit = async (originalDate: string) => {
    if (!bankCode || !contractNumber || saving) return;
    const amortized = Number(editAmortized.replace(",", "."));
    const interest = Number(editInterest.replace(",", "."));
    if (!/^\d{4}-\d{2}-\d{2}$/.test(editDate) || !Number.isFinite(amortized) || !Number.isFinite(interest)) {
      setEditError("Fecha (AAAA-MM-DD) e importes numéricos requeridos.");
      return;
    }
    setSaving(true);
    setEditError(null);
    try {
      await editLoanPendingInstallment(bankCode, contractNumber, originalDate, {
        date: editDate,
        amortized,
        interest,
      });
      setEditingDate(null);
      await onEdited?.();
    } catch (e: unknown) {
      setEditError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <table className="data">
      <thead>
        <tr>
          <th>Fecha</th>
          <th className="num">Capital amort.</th>
          <th className="num">Intereses</th>
          <th className="num">Total</th>
          <th className="num">Pagado</th>
          <th className="num">Capital pendiente</th>
          <th>Estado</th>
          <th>Tipo</th>
          {editable && <th>Acciones</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const est = isEstimated(r.operationType);
          const isPartial = r.status === "partial";
          const isPaidByApp = r.status === "paid";
          const rowStyle: React.CSSProperties = {
            ...(est ? { fontStyle: "italic", opacity: 0.75 } : {}),
            ...(isPartial ? { background: "rgba(255,165,0,0.10)" } : {}),
            ...(isPaidByApp ? { background: "rgba(80,200,120,0.08)" } : {}),
          };
          const paidAmount = r.paidAmount ?? 0;
          const isEditingRow = editable && editingDate === r.date;
          return (
            <tr key={`${r.date}-${r.operationType}-${i}`} style={rowStyle}>
              <td>
                {isEditingRow ? (
                  <input
                    type="text"
                    value={editDate}
                    onChange={(e) => setEditDate(e.target.value)}
                    style={{ width: "7em" }}
                  />
                ) : (
                  r.date
                )}
              </td>
              <td className="num">
                {isEditingRow ? (
                  <input
                    type="text"
                    value={editAmortized}
                    onChange={(e) => setEditAmortized(e.target.value)}
                    style={{ width: "6em" }}
                  />
                ) : (
                  formatMoney(r.amortized, currency)
                )}
              </td>
              <td className="num">
                {isEditingRow ? (
                  <input
                    type="text"
                    value={editInterest}
                    onChange={(e) => setEditInterest(e.target.value)}
                    style={{ width: "6em" }}
                  />
                ) : (
                  formatMoney(r.interest, currency)
                )}
              </td>
              <td className="num">
                {isEditingRow
                  ? formatMoney(
                      (Number(editAmortized.replace(",", ".")) || 0) +
                        (Number(editInterest.replace(",", ".")) || 0),
                      currency,
                    )
                  : formatMoney(r.total, currency)}
              </td>
              <td className="num">
                {paidAmount > 0 || isPaidByApp ? (
                  formatMoney(isPaidByApp ? r.total : paidAmount, currency)
                ) : (
                  <span className="meta">—</span>
                )}
              </td>
              <td className="num">{formatMoney(r.pendingCapital, currency)}</td>
              <td>
                {isPartial ? (
                  <span
                    style={{
                      background: "rgba(255,165,0,0.18)",
                      border: "1px solid rgba(255,140,0,0.5)",
                      borderRadius: 4,
                      padding: "1px 6px",
                      fontSize: "0.8em",
                    }}
                    title={`Cargada ${formatMoney(paidAmount, currency)} de ${formatMoney(r.total, currency)}. Pendiente: ${formatMoney(r.total - paidAmount, currency)} (cuenta puente).`}
                  >
                    PARCIAL {formatMoney(paidAmount, currency)} / {formatMoney(r.total, currency)}
                  </span>
                ) : isPaidByApp ? (
                  <span
                    style={{
                      background: "rgba(80,200,120,0.18)",
                      border: "1px solid rgba(60,160,90,0.5)",
                      borderRadius: 4,
                      padding: "1px 6px",
                      fontSize: "0.8em",
                    }}
                    title={r.paidJdtNum ? `JdtNum ${r.paidJdtNum}` : "Contabilizada por la app"}
                  >
                    PAGADA
                  </span>
                ) : (
                  <span className="meta">—</span>
                )}
              </td>
              <td>
                {opLabel(r.operationType)}
                {est && (
                  <span className="meta" style={{ marginLeft: "0.3rem", fontSize: "0.8em" }}>
                    (proyectada)
                  </span>
                )}
              </td>
              {editable && (
                <td>
                  {isEditingRow ? (
                    <div style={{ display: "flex", gap: "0.3rem", alignItems: "center" }}>
                      <button type="button" onClick={() => void saveEdit(r.date)} disabled={saving}>
                        {saving ? "…" : "Guardar"}
                      </button>
                      <button type="button" onClick={cancelEdit} disabled={saving}>
                        Cancelar
                      </button>
                      {editError && (
                        <span className="error" style={{ fontSize: "0.8em" }}>
                          {editError}
                        </span>
                      )}
                    </div>
                  ) : (
                    <button type="button" onClick={() => startEdit(r)} title="Corregir esta cuota">
                      ✎
                    </button>
                  )}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
